import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Minimize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { SupportChatWindow } from "./SupportChatWindow";
import { useMyActiveSession, useChatMessages, useChatMessagesSubscription } from "@/hooks/useSupportChat";

export const SupportChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastSeenAt, setLastSeenAt] = useState<string | null>(null);
  const { user } = useAuth();
  const { data: activeSession } = useMyActiveSession();
  const { data: messages = [] } = useChatMessages(activeSession?.id);

  useChatMessagesSubscription(activeSession?.id);

  useEffect(() => {
    setUnreadCount(0);
    setLastSeenAt(null);
  }, [activeSession?.id]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      if (messages.length > 0) {
        setLastSeenAt(messages[messages.length - 1].created_at);
      }
      setUnreadCount(0);
      return;
    }

    if (messages.length === 0) {
      setUnreadCount(0);
      return;
    }

    const baseTime = lastSeenAt ? new Date(lastSeenAt) : null;
    const unread = messages.filter((message) => {
      if (message.sender_type !== "admin") return false;
      if (!baseTime) return true;
      return new Date(message.created_at) > baseTime;
    }).length;

    setUnreadCount(unread);
  }, [messages, isOpen, isMinimized, lastSeenAt]);

  // Don't show widget if user is not logged in
  if (!user) return null;

  const handleToggle = () => {
    if (isMinimized) {
      setIsMinimized(false);
    } else {
      setIsOpen(!isOpen);
    }
  };

  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsMinimized(false);
  };

  return (
    <>
      {/* Floating Chat Bubble */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="fixed bottom-4 right-4 z-50 safe-area-bottom"
          >
            <Button
              onClick={handleToggle}
              className="h-14 w-14 rounded-full shadow-lg bg-gradient-to-br from-primary to-coral hover:from-primary/90 hover:to-coral/90 text-white"
              size="icon"
            >
              <MessageCircle className="h-6 w-6" />
              {(unreadCount > 0 || activeSession) && (
                <Badge 
                  className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-destructive text-destructive-foreground text-xs"
                >
                  {unreadCount || "•"}
                </Badge>
              )}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              height: isMinimized ? 56 : "auto"
            }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-4 right-4 z-50 w-[calc(100vw-2rem)] sm:w-96 safe-area-bottom"
          >
            <div className="bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary/10 to-coral/10 border-b border-border">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-coral flex items-center justify-center">
                    <MessageCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold">Support E-tickets</h3>
                    <p className="text-xs text-muted-foreground">
                      {activeSession?.status === "active" 
                        ? "En conversation" 
                        : activeSession?.status === "waiting"
                        ? "En attente d'un agent..."
                        : "Nous sommes là pour vous aider"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleMinimize}
                  >
                    <Minimize2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleClose}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Chat Content */}
              {!isMinimized && (
                <SupportChatWindow 
                  session={activeSession}
                  onEscalateToTicket={() => {
                    // Will be handled inside
                  }}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
